import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { X, Loader2, AlertTriangle } from 'lucide-react'

interface TestKOModalProps {
  isOpen: boolean
  onClose: () => void
  onConfirm: (issueDescription: string) => void
  isLoading: boolean
  userStoryTitle: string
}

export function TestKOModal({
  isOpen,
  onClose,
  onConfirm,
  isLoading,
  userStoryTitle,
}: TestKOModalProps) {
  const { t } = useTranslation()
  const [issueDescription, setIssueDescription] = useState('')

  if (!isOpen) return null

  const handleConfirm = () => {
    if (issueDescription.trim()) {
      onConfirm(issueDescription.trim())
    }
  }

  const handleClose = () => {
    if (!isLoading) {
      setIssueDescription('')
      onClose()
    }
  }

  const isValid = issueDescription.trim().length > 0

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/30"
      onClick={handleClose}
    >
      <div
        className="flex w-[520px] max-w-[90vw] flex-col overflow-hidden rounded-xl bg-white shadow-xl animate-in fade-in zoom-in-95 duration-200"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between border-b border-border px-6 py-4">
          <h2 className="text-lg font-semibold text-foreground">
            {t('us.testKoTitle')}
          </h2>
          <button
            onClick={handleClose}
            disabled={isLoading}
            className="flex h-8 w-8 items-center justify-center rounded-md text-foreground-muted transition-colors hover:bg-background-secondary hover:text-foreground disabled:opacity-50"
          >
            <X className="h-[18px] w-[18px]" />
          </button>
        </div>

        {/* Content */}
        <div className="flex flex-col gap-4 p-6">
          <p className="text-sm text-foreground-muted">
            {t('us.testKoDescription', { title: userStoryTitle })}
          </p>

          {/* Warning notice */}
          <div className="flex items-start gap-3 rounded-lg bg-amber-50 p-4">
            <AlertTriangle className="h-5 w-5 flex-shrink-0 text-amber-600" />
            <p className="text-sm text-amber-800">
              {t('us.testKoWarning')}
            </p>
          </div>

          <div className="flex flex-col gap-2">
            <label htmlFor="issue-description" className="text-sm font-medium text-foreground">
              {t('us.testKoIssueLabel')}
            </label>
            <textarea
              id="issue-description"
              value={issueDescription}
              onChange={(e) => setIssueDescription(e.target.value)}
              placeholder={t('us.testKoIssuePlaceholder')}
              disabled={isLoading}
              rows={4}
              className="w-full resize-none rounded-lg border border-border bg-background-secondary px-4 py-3 text-sm placeholder:text-foreground-subtle focus:border-accent focus:outline-none disabled:opacity-50"
            />
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 border-t border-border px-6 py-4">
          <button
            onClick={handleClose}
            disabled={isLoading}
            className="rounded-lg border border-border bg-white px-4 py-2.5 text-sm font-medium text-foreground transition-colors hover:bg-background-secondary disabled:opacity-50"
          >
            {t('common.cancel')}
          </button>
          <button
            onClick={handleConfirm}
            disabled={!isValid || isLoading}
            className="flex items-center gap-2 rounded-lg bg-destructive px-4 py-2.5 text-sm font-medium text-white transition-colors hover:bg-destructive/90 disabled:opacity-50"
          >
            {isLoading && <Loader2 className="h-4 w-4 animate-spin" />}
            {t('us.testKoConfirm')}
          </button>
        </div>
      </div>
    </div>
  )
}
